import { useEffect, useState } from 'react';
import Icon from '../ui/Icon';
import FieldError from '../ui/FieldError';
import { DATE_RANGES, SOURCES } from './feedbackMeta';
import { isIsoDate, MAX_SEARCH_LENGTH, MIN_SEARCH_LENGTH } from './feedbackParams';

const control =
  'h-10 bg-surface-container-lowest border border-outline-variant/50 rounded-lg text-[13px] text-on-surface outline-none transition focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]';

function Select({ id, label, value, onChange, children, className = '' }) {
  return (
    <div className={`relative ${className}`}>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={`${control} w-full appearance-none pl-3 pr-8 font-semibold`}>
        {children}
      </select>
      <Icon name="expand_more" size={18} className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-on-surface-variant" />
    </div>
  );
}

/** Search, date range (preset or custom from/to), source and refresh. */
export default function FeedbackFilters({ params, setParams, searchInput, setSearchInput, onRefresh, refreshing }) {
  const urlRange = params.from ? 'custom' : params.days;
  const [range, setRange] = useState(urlRange);
  const [from, setFrom] = useState(params.from);
  const [to, setTo] = useState(params.to);
  useEffect(() => {
    setRange(urlRange);
    setFrom(params.from);
    setTo(params.to);
  }, [urlRange, params.from, params.to]);

  const rangeError = range === 'custom' && isIsoDate(from) && isIsoDate(to) && from > to ? "The start date can't be after the end date." : undefined;

  const applyCustom = (nextFrom, nextTo) => {
    if (isIsoDate(nextFrom) && isIsoDate(nextTo) && nextFrom <= nextTo) setParams({ from: nextFrom, to: nextTo });
  };

  const tooShort = searchInput.trim().length > 0 && searchInput.trim().length < MIN_SEARCH_LENGTH;

  return (
    <section className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs p-3 flex flex-col gap-3">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1" role="search">
          <label htmlFor="feedback-search" className="sr-only">
            Search feedback
          </label>
          <Icon name="search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none" />
          <input
            id="feedback-search"
            type="text"
            autoComplete="off"
            maxLength={MAX_SEARCH_LENGTH}
            placeholder="Search by subject, customer name, email, or keywords…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-describedby={tooShort ? 'feedback-search-hint' : undefined}
            className={`${control} w-full pl-9 pr-9 placeholder:text-on-surface-variant/60`}
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-on-surface-variant hover:text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
            >
              <Icon name="close" size={16} />
            </button>
          )}
          {tooShort && (
            <p id="feedback-search-hint" className="absolute left-0 top-full mt-0.5 text-[11px] text-on-surface-variant">
              Type at least {MIN_SEARCH_LENGTH} characters
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            id="feedback-range"
            label="Date range"
            value={range}
            className="w-40"
            onChange={(value) => {
              setRange(value);
              if (value === 'custom') applyCustom(from, to);
              else setParams({ days: value, from: '', to: '' });
            }}
          >
            {DATE_RANGES.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </Select>
          <Select id="feedback-source" label="Source" value={params.source} className="w-36" onChange={(source) => setParams({ source })}>
            <option value="all">All sources</option>
            {SOURCES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </Select>
          <button
            type="button"
            onClick={onRefresh}
            aria-label="Refresh feedback"
            title="Refresh"
            className="w-10 h-10 rounded-lg flex items-center justify-center text-on-surface-variant hover:bg-surface-container hover:text-on-surface outline-none focus-visible:ring-2 focus-visible:ring-status-ok-dot"
          >
            <Icon name="refresh" size={20} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {range === 'custom' && (
        <div className="flex flex-wrap items-end gap-3">
          {[
            ['feedback-from', 'From', from, setFrom, (v) => applyCustom(v, to)],
            ['feedback-to', 'To', to, setTo, (v) => applyCustom(from, v)],
          ].map(([id, label, value, set, apply]) => (
            <div key={id}>
              <label htmlFor={id} className="block text-[11px] font-semibold uppercase tracking-wider text-on-surface-variant mb-1">
                {label}
              </label>
              <input
                id={id}
                type="date"
                value={value}
                onChange={(e) => {
                  set(e.target.value);
                  apply(e.target.value);
                }}
                aria-invalid={Boolean(rangeError)}
                aria-describedby={rangeError ? 'feedback-range-error' : undefined}
                className={`${control} px-3 font-data-mono ${rangeError ? 'border-error' : ''}`}
              />
            </div>
          ))}
          {!rangeError && !(params.from && params.to) && <p className="text-xs text-on-surface-variant pb-2.5">Pick both dates to apply the range.</p>}
          <FieldError id="feedback-range-error">{rangeError}</FieldError>
        </div>
      )}
    </section>
  );
}
